import requestCache from '../utils/request-cache';

import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';
import QueryFilter from '../helpers/query-filter';
import type { RootState } from '../reducers';
import { trackAssignmentRemoval } from './tracking';

import type { ElasticSearchResponse } from '../@types/ElasticSearchResponse';
import type { Retailer } from '../@types/Retailer';
import type { BaseAction } from '../@types/actions';
import type { Assignment, AssignmentIn, AssignmentPublishProps } from '../@types/assignment';
import type { Stakeholder } from '../@types/stakeholder';
import * as http from '../Http';
import {
  buildAuthorizedDeleteRequest,
  buildAuthorizedGetRequest,
  buildAuthorizedPostRequest,
} from '../Http/requestTemplates';

export const REQ_ASSIGNMENT = 'REQ/ASSIGNMENT';
export const RCV_ASSIGNMENT = 'RCV/ASSIGNMENT';
type REQ_ASSIGNMENT_TYPE = BaseAction<typeof REQ_ASSIGNMENT>;
type RCV_ASSIGNMENT_TYPE = BaseAction<typeof RCV_ASSIGNMENT> & {
  response: ElasticSearchResponse<AssignmentIn>;
};

export const REQ_ASSIGNMENT_RECIPIENTS = 'REQ/ASSIGNMENT_RECIPIENT_LIST';
export const RCV_ASSIGNMENT_RECIPIENTS = 'RCV/ASSIGNMENT_RECIPIENT_LIST';
type REQ_ASSIGNMENT_RECIPIENTS_TYPE = BaseAction<typeof REQ_ASSIGNMENT_RECIPIENTS>;
type RCV_ASSIGNMENT_RECIPIENTS_TYPE = BaseAction<typeof RCV_ASSIGNMENT_RECIPIENTS> & {
  response: ElasticSearchResponse<Retailer>;
};

export const REQ_ASSIGNMENT_STAKEHOLDERS = 'REQ/ASSIGNMENT_STAKEHOLDER_LIST';
export const RCV_ASSIGNMENT_STAKEHOLDERS = 'RCV/ASSIGNMENT_STAKEHOLDER_LIST';
type REQ_ASSIGNMENT_STAKEHOLDERS_TYPE = BaseAction<typeof REQ_ASSIGNMENT_STAKEHOLDERS>;
type RCV_ASSIGNMENT_STAKEHOLDERS_TYPE = BaseAction<typeof RCV_ASSIGNMENT_STAKEHOLDERS> & {
  response: Stakeholder[];
  id: string;
};

export const REQ_ASSIGNMENTS_LIST = 'REQ/ASSIGNMENTS_LIST';
export const RCV_ASSIGNMENTS_LIST = 'RCV/ASSIGNMENTS_LIST';
type REQ_ASSIGNMENTS_LIST_TYPE = BaseAction<typeof REQ_ASSIGNMENTS_LIST>;
type RCV_ASSIGNMENTS_LIST_TYPE = BaseAction<typeof RCV_ASSIGNMENTS_LIST> & {
  response: ElasticSearchResponse<AssignmentIn>;
};

export const REQ_REMOVE_ASSIGNMENT = 'REQ/REMOVE_ASSIGNMENT';
export const RCV_REMOVE_ASSIGNMENT = 'RCV/REMOVE_ASSIGNMENT';
type REQ_REMOVE_ASSIGNMENT_TYPE = BaseAction<typeof REQ_REMOVE_ASSIGNMENT>;
type RCV_REMOVE_ASSIGNMENT_TYPE = BaseAction<typeof RCV_REMOVE_ASSIGNMENT> & {
  response: { id: string };
};

export const REQ_CREATE_ASSIGNMENT = 'REQ/CREATE_ASSIGNMENT';
export const RCV_CREATE_ASSIGNMENT = 'RCV/CREATE_ASSIGNMENT';
type REQ_CREATE_ASSIGNMENT_TYPE = BaseAction<typeof REQ_CREATE_ASSIGNMENT>;
type RCV_CREATE_ASSIGNMENT_TYPE = BaseAction<typeof RCV_CREATE_ASSIGNMENT> & {
  response: AssignmentPublishProps;
};

export const REQ_CLEAR_ASSIGNMENTS = 'REQ/CLEAR_ASSIGNMENTS';
type REQ_CLEAR_ASSIGNMENTS_TYPE = BaseAction<typeof REQ_CLEAR_ASSIGNMENTS>;

export const REQUEST_FAILED = 'ASSIGNMENT/REQUEST_FAILED';
type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED>;

export const REQ_CLEAR_CREATED_ASSIGNMENT = 'REQ/CLEAR_CREATED_ASSIGNMENT';
type REQ_CLEAR_CREATED_ASSIGNMENT_TYPE = BaseAction<typeof REQ_CLEAR_CREATED_ASSIGNMENT>;

export type ASSIGNMENT_ACTION =
  | REQ_ASSIGNMENT_TYPE
  | RCV_ASSIGNMENT_TYPE
  | REQ_ASSIGNMENT_RECIPIENTS_TYPE
  | RCV_ASSIGNMENT_RECIPIENTS_TYPE
  | REQ_ASSIGNMENT_STAKEHOLDERS_TYPE
  | RCV_ASSIGNMENT_STAKEHOLDERS_TYPE
  | REQ_ASSIGNMENTS_LIST_TYPE
  | RCV_ASSIGNMENTS_LIST_TYPE
  | REQ_REMOVE_ASSIGNMENT_TYPE
  | RCV_REMOVE_ASSIGNMENT_TYPE
  | REQ_CREATE_ASSIGNMENT_TYPE
  | RCV_CREATE_ASSIGNMENT_TYPE
  | REQ_CLEAR_ASSIGNMENTS_TYPE
  | REQ_CLEAR_CREATED_ASSIGNMENT_TYPE
  | REQUEST_FAILED_TYPE;

type Dispatch = ReduxDispatch<ASSIGNMENT_ACTION>;

export const all = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { auth } = getState();
  if (!auth.token || requestCache.isPending(REQ_ASSIGNMENTS_LIST)) {
    return;
  }
  requestCache.isPending(REQ_ASSIGNMENTS_LIST, true);

  try {
    dispatch({ type: REQ_ASSIGNMENTS_LIST });

    const response = await http.json<ElasticSearchResponse<AssignmentIn>>(
      '/api/assignments',
      buildAuthorizedGetRequest(auth.token),
    );
    http.validateResponse(response);

    dispatch({
      type: RCV_ASSIGNMENTS_LIST,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_ASSIGNMENTS_LIST });
  } finally {
    requestCache.isPending(REQ_ASSIGNMENTS_LIST, false);
  }
};

export const remove =
  (
    id: string,
    trackData: { information: Assignment & { reason: string; timeUntilSold: string } },
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_REMOVE_ASSIGNMENT)) {
      return;
    }
    requestCache.isPending(REQ_REMOVE_ASSIGNMENT, true);

    try {
      dispatch({ type: REQ_REMOVE_ASSIGNMENT });

      const response = await http.json<{ id: string }>(
        `/api/assignments/${id}`,
        buildAuthorizedDeleteRequest(auth.token),
      );
      http.validateResponse(response);
      dispatch({
        type: RCV_REMOVE_ASSIGNMENT,
        response: response.response,
      });
      trackAssignmentRemoval(trackData)(dispatch, getState);
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_REMOVE_ASSIGNMENT });
    } finally {
      requestCache.isPending(REQ_REMOVE_ASSIGNMENT, false);
    }
  };

export const create =
  (assignment: AssignmentPublishProps) => async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_CREATE_ASSIGNMENT)) {
      return;
    }
    requestCache.isPending(REQ_CREATE_ASSIGNMENT, true);

    try {
      dispatch({ type: REQ_CREATE_ASSIGNMENT });

      const response = await http.json<AssignmentPublishProps>(
        '/api/assignments',
        buildAuthorizedPostRequest(auth.token, { content: assignment }),
      );
      http.validateResponse(response);
      dispatch({
        type: RCV_CREATE_ASSIGNMENT,
        response: response.response,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_CREATE_ASSIGNMENT });
    } finally {
      requestCache.isPending(REQ_CREATE_ASSIGNMENT, false);
    }
  };

export const get = (id: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const { auth } = getState();
  if (!auth.token || requestCache.isPending(REQ_ASSIGNMENT)) {
    return;
  }
  requestCache.isPending(REQ_ASSIGNMENT, true);

  try {
    dispatch({ type: REQ_ASSIGNMENT });

    const response = await http.json<ElasticSearchResponse<AssignmentIn>>(
      `/api/assignments/${id}`,
      buildAuthorizedGetRequest(auth.token),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_ASSIGNMENT,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_ASSIGNMENT });
  } finally {
    requestCache.isPending(REQ_ASSIGNMENT, false);
  }
};

export const getRecipientCount = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { auth } = getState();
  if (!auth.token || requestCache.isPending(REQ_ASSIGNMENT_RECIPIENTS)) {
    return;
  }
  requestCache.isPending(REQ_ASSIGNMENT_RECIPIENTS, true);

  try {
    const queryFilter = QueryFilter.create().add('acceptsAssignments', 'true').setPaginationSize(0);

    dispatch({ type: REQ_ASSIGNMENT_RECIPIENTS });

    const response = await http.json<ElasticSearchResponse<Retailer>>(
      `/api/retailers?filter=${queryFilter.toQueryStringFilter()}`,
      buildAuthorizedGetRequest(auth.token),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_ASSIGNMENT_RECIPIENTS,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_ASSIGNMENT_RECIPIENTS });
  } finally {
    requestCache.isPending(REQ_ASSIGNMENT_RECIPIENTS, false);
  }
};

export const getStakeholders =
  (id: string) => async (dispatch: Dispatch, getState: () => RootState) => {
    const key = `${REQ_ASSIGNMENT_STAKEHOLDERS}/${id}`;

    const { auth } = getState();
    if (!auth.token || requestCache.isPending(key)) {
      return;
    }
    requestCache.isPending(key, true);

    try {
      dispatch({ type: REQ_ASSIGNMENT_STAKEHOLDERS });

      const response = await http.json<Stakeholder[]>(
        `/api/assignments/${id}/stakeholders`,
        buildAuthorizedGetRequest(auth.token),
      );
      http.validateResponse(response);
      dispatch({
        type: RCV_ASSIGNMENT_STAKEHOLDERS,
        response: response.response,
        id,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_ASSIGNMENT_STAKEHOLDERS });
    } finally {
      requestCache.isPending(key, false);
    }
  };

export const clear = () => (dispatch: Dispatch) => dispatch({ type: REQ_CLEAR_ASSIGNMENTS });

export const resetCreatedItem = () => (dispatch: Dispatch) =>
  dispatch({ type: REQ_CLEAR_CREATED_ASSIGNMENT });
